import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import backgroundImage from "../../assets/images/apply-now.png"; // ✅ Correct path
import contactImage from "../../assets/images/Rectangle 5 (1).png"; // ✅ Correct path

// -------------------- Theme --------------------
const theme = createTheme({
  typography: {
    fontFamily: "Inter, sans-serif",
    h4: { fontWeight: 700 },
    h6: { fontWeight: 600 },
  },
  palette: {
    primary: { main: "#1E3A8A" },
    background: { default: "#F9FAFB" },
  },
});

// -------------------- Styled Components --------------------
const Banner = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: "auto",
  borderRadius: "16px",
  overflow: "hidden",
  textAlign: "center",
  color: "#fff",
  marginBottom: theme.spacing(6),
  [theme.breakpoints.down("sm")]: {
    marginBottom: theme.spacing(4),
  },
}));

const BannerImage = styled("img")(({ theme }) => ({
  width: "100%",
  height: "auto",
  objectFit: "cover",
  borderRadius: "16px",
  [theme.breakpoints.up("xs")]: {
    maxHeight: 200,
  },
  [theme.breakpoints.up("sm")]: {
    maxHeight: 280,
  },
  [theme.breakpoints.up("md")]: {
    maxHeight: 350,
  },
  [theme.breakpoints.up("lg")]: {
    maxHeight: 420,
  },
}));

const InfoCard = styled(Card)(({ theme }) => ({
  borderRadius: "16px",
  boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
  },
}));

// -------------------- Component --------------------
function ContactUs() {
  const lang = useSelector((state) => state.language.lang);

  const translations = {
    en: {
      title: "Contact Us",
      hotline: "Hotline",
      hotlineDetail: "+959443734550, +959443734551, +959443734552",
      office: "Head Office",
      officeDetail:
        "No.53, Strand Road, between Bo Son Pat street and 32nd street, Pabedan Township, Yangon.",
      email: "Email Address",
      emailDetail: "enquiry@momofinances.com",
      hours: "Business Hours",
      hoursDetail: "Monday to Saturday\n8:30 AM - 4:30 PM",
      support: "Need Help? We're here for you!",
    },
    mm: {
      title: "ဆက်သွယ်ရန်",
      hotline: "အချက်အလက်မေးမြန်းရန်ဖုန်းနံပါတ်",
      hotlineDetail: "+959443734550, +959443734551, +959443734552",
      office: "ရုံးချုပ် လိပ်စာ",
      officeDetail:
        "အမှတ်(၅၃)၊ ကုန်သည်လမ်း၊ ဗိုလ်ဆွန်ပတ်လမ်းနှင့် ၃၂ လမ်းကြား၊ ပန်းပဲတန်းမြို့နယ်၊ ရန်ကုန်မြို့။",
      email: "အီးမေးလ်လိပ်စာ",
      emailDetail: "enquiry@momofinances.com",
      hours: "ဝန်ဆောင်မှု ဖွင့်ချိန်",
      hoursDetail: "တနင်္လာမှ စနေနေ့အထိ\nမနက် ၈:၃၀ မှ ညနေ ၄:၃၀ အထိ",
      support: "အကူအညီလိုပါသလား။ ကျွန်တော်တို့က အဆင်သင့်ပါ!",
    },
  };

  const t = translations[lang];

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ backgroundColor: "#EFF6FF", py: { xs: 4, md: 8 } }}>
        {/* ✅ Responsive Banner */}
        <Banner>
          <BannerImage src={backgroundImage} alt="Contact Banner" />
        </Banner>

        <Container>
          {/* ✅ Contact Info Section */}
          <Grid container spacing={6} alignItems="center" justifyContent="center">
            {/* Contact Details */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <InfoCard>
                    <CardContent>
                      <Typography variant="h6" color="primary">
                        📞 {t.hotline}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 1, whiteSpace: "pre-line" }}
                      >
                        {t.hotlineDetail}
                      </Typography>
                    </CardContent>
                  </InfoCard>
                </Grid>

                <Grid item xs={12}>
                  <InfoCard>
                    <CardContent>
                      <Typography variant="h6" color="primary">
                        🏢 {t.office}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {t.officeDetail}
                      </Typography>
                    </CardContent>
                  </InfoCard>
                </Grid>

                <Grid item xs={12}>
                  <InfoCard>
                    <CardContent>
                      <Typography variant="h6" color="primary">
                        ✉️ {t.email}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {t.emailDetail}
                      </Typography>
                    </CardContent>
                  </InfoCard>
                </Grid>

                <Grid item xs={12}>
                  <InfoCard>
                    <CardContent>
                      <Typography variant="h6" color="primary">
                        ⏰ {t.hours}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 1, whiteSpace: "pre-line" }}
                      >
                        {t.hoursDetail}
                      </Typography>
                    </CardContent>
                  </InfoCard>
                </Grid>
              </Grid>
            </Grid>

            {/* ✅ Right Side Responsive Image */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  gap: 2,
                }}
              >
                <Box
                  component="img"
                  src={contactImage}
                  alt="Contact Illustration"
                  sx={{
                    width: "100%",
                    height: "auto",
                    maxWidth: {
                      xs: 260,
                      sm: 320,
                      md: 400,
                      lg: 460,
                    },
                    borderRadius: "12px",
                    objectFit: "cover",
                  }}
                />
                <Typography
                  variant="h6"
                  color="primary"
                  sx={{
                    mt: 2,
                    fontSize: { xs: "1rem", md: "1.2rem" },
                  }}
                >
                  {t.support}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default ContactUs;
